import apiService from '../api';

const quizApiTimeUtilsMixin = {
  /**
   * Get assignment status based on attempt data
   */
  getAssignmentStatus(assignment) {
    // The status should come from the backend, but if not available, calculate it
    if (assignment.status) {
      // Check if it's overdue based on due date
      const dueAt = assignment.assignment_due_at || assignment.quiz_due_at;
      if (assignment.status !== 'completed' && this.isOverdue(dueAt)) {
        return 'overdue';
      }
      return assignment.status;
    }

    // Fallback calculation
    const dueAt = assignment.assignment_due_at || assignment.quiz_due_at;

    if (assignment.submitted_at) {
      // Check if submission was made after due date
      const submittedAt = new Date(assignment.submitted_at);
      const dueDate = new Date(dueAt);
      if (dueAt && submittedAt > dueDate) {
        return 'late';
      }
      return 'completed';
    } else if (assignment.started_at) {
      return this.isOverdue(dueAt) ? 'overdue' : 'in_progress';
    } else {
      return this.isOverdue(dueAt) ? 'overdue' : 'not_started';
    }
  },

  /**
   * Check if assignment is overdue
   */
  isOverdue(dueAt) {
    if (!dueAt) return false;

    try {
      const dueDate = new Date(dueAt);
      const now = new Date();
      return now > dueDate;
    } catch (error) {
      console.error('Error checking if overdue:', error);
      return false;
    }
  },

  /**
   * Check if assignment is due soon (within 24 hours)
   */
  isDueSoon(dueAt) {
    if (!dueAt) return false;

    try {
      const dueDate = new Date(dueAt);
      const now = new Date();
      const diffMs = dueDate - now;
      const diffHours = diffMs / (1000 * 60 * 60);

      return diffHours > 0 && diffHours <= 24;
    } catch (error) {
      console.error('Error checking if due soon:', error);
      return false;
    }
  },

  /**
   * Get server time to sync client timer
   */
  async getServerTime() {
    try {
      const response = await apiService.request('/config/server-time');
      if (response.success && response.data && response.data.serverTime) {
        return new Date(response.data.serverTime);
      }
      // Fallback to current time if endpoint not available
      return new Date();
    } catch (error) {
      console.warn('Failed to get server time, using client time:', error);
      return new Date();
    }
  },

  /**
   * Calculate time remaining for an attempt with server time sync
   */
  async calculateTimeRemaining(startedAt, timeLimitSeconds, serverTime = null) {
    if (!startedAt || !timeLimitSeconds) return null;

    try {
      const startTime = new Date(startedAt);
      // Use server time if provided, otherwise get it
      let now;
      if (serverTime) {
        now = new Date(serverTime);
      } else {
        // Try to get server time, but don't block if it fails
        try {
          now = await this.getServerTime();
        } catch (e) {
          // Fallback to client time
          now = new Date();
        }
      }

      const elapsed = Math.floor((now - startTime) / 1000);
      const remaining = timeLimitSeconds - elapsed;

      return Math.max(0, remaining);
    } catch (error) {
      console.error('Error calculating time remaining:', error);
      return null;
    }
  },

  /**
   * Calculate time remaining synchronously (for timer updates)
   * Uses client time but accounts for clock skew if serverTimeOffset is provided
   */
  calculateTimeRemainingSync(startedAt, timeLimitSeconds, serverTimeOffset = 0) {
    if (!startedAt || !timeLimitSeconds) return null;

    try {
      const startTime = new Date(startedAt);
      const now = new Date(Date.now() + serverTimeOffset); // Apply offset to account for clock skew
      const elapsed = Math.floor((now - startTime) / 1000);
      const remaining = timeLimitSeconds - elapsed;

      return Math.max(0, remaining);
    } catch (error) {
      console.error('Error calculating time remaining:', error);
      return null;
    }
  },

  /**
   * Format time remaining in seconds to a readable string
   */
  formatTimeRemaining(seconds) {
    if (seconds === null || seconds === undefined) return 'No time limit';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else {
      return `${remainingSeconds}s`;
    }
  },
};

export default quizApiTimeUtilsMixin;
